import React, { ComponentProps, useEffect, useMemo, useState } from 'react';
import { elementToDataURL, height, StateRenderer, width } from './Preview';
import Highcharts from 'highcharts';
import { merge } from 'highcharts';
import { TopixData } from 'components/features/home/assetStuffing/AssetStuffing';

export type RenderedChartToElementType<State> = (props: {
  renderedChart: React.ReactElement | null;
  state: State;
  range: ChartRange;
  title: string;
  width: number | null;
  height: number | null;
}) => React.ReactElement | null;

type ChartRange = { min: Date; max: Date } | null;

export type SetRange = React.Dispatch<React.SetStateAction<ChartRange>>;

export type ChartForPreviewType<Data> = (props: {
  setChartDataURL: (chartDataURL: string | null) => void;
  options: Highcharts.Options;
  range: ChartRange;
  data: Data;
  topixData: TopixData[];
}) => React.ReactElement | null;
export interface UseChartRenderer<Data, State> {
  useChartRenderer: (props: {
    Chart: ChartForPreviewType<Data>;
    stateToOptions: (state: State) => Highcharts.Options;
    RenderedChartToElement: RenderedChartToElementType<State>;
    chartTitle: string;
  }) => {
    StateRenderer: StateRenderer<State, Data>;
    setRange: SetRange;
  };
}

// dataの変更のみ追従
export const useChartRenderer = <State, Data>({
  Chart,
  stateToOptions,
  RenderedChartToElement,
  chartTitle,
}: Parameters<
  UseChartRenderer<Data, State>['useChartRenderer']
>[0]): ReturnType<UseChartRenderer<Data, State>['useChartRenderer']> => {
  const [range, setRange] = useState<ChartRange>(null);
  const StateRenderer: StateRenderer<State, Data> = function StateRenderer({
    setDataURL,
    state,
    extra,
    topixExtra,
  }) {
    const [chartDataURL, setChartDataURL] = useState<
      | { tag: 'chartInstanceLoading' }
      | { tag: 'imperfectlyDrawnChart' }
      | { tag: 'perfectlyDrawnChart'; value: string }
    >({ tag: 'chartInstanceLoading' });
    useEffect(() => {
      if (chartDataURL.tag !== 'perfectlyDrawnChart') return;
      const getElementChartRendered = (dataURL: string): React.ReactElement => {
        return (
          <RenderedChartToElement
            renderedChart={
              <img src={dataURL} alt="" style={{ width: '100%' }} />
            }
            state={state}
            range={range}
            title={chartTitle}
            width={width}
            height={height}
          />
        );
      };
      (async () => {
        const dataURL = await elementToDataURL(
          getElementChartRendered(chartDataURL.value)
        );
        setDataURL(dataURL);
      })();
    }, [chartDataURL, setDataURL, state]);
    const renderedChart = useMemo(() => {
      return (
        <Chart
          data={extra}
          topixData={topixExtra}
          options={merge<Highcharts.Options>(
            {
              navigator: { enabled: false },
              exporting: { enabled: false },
              rangeSelector: { enabled: false, selected: undefined },
              legend: { enabled: true, verticalAlign: 'top' },
              chart: {
                className: 'cursor-default',
                panning: {
                  enabled: false,
                },
                width,
                height,
              },
            },
            stateToOptions(state)
          )}
          setChartDataURL={chartDataURL => {
            if (!chartDataURL) {
              setChartDataURL({ tag: 'imperfectlyDrawnChart' });
              return;
            }
            setChartDataURL(prev => {
              switch (prev.tag) {
                case 'chartInstanceLoading':
                  return {
                    tag: 'imperfectlyDrawnChart',
                  };
                case 'imperfectlyDrawnChart':
                case 'perfectlyDrawnChart':
                  return {
                    tag: 'perfectlyDrawnChart',
                    value: chartDataURL,
                  };
              }
            });
          }}
          range={range}
        />
      );

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, extra]);
    const rendered = useMemo(() => {
      return (
        <div
          className="py-6 grid items-center justify-center"
          style={{ backgroundColor: '#52525b' }}>
          <RenderedChartToElement
            range={range}
            renderedChart={renderedChart}
            state={state}
            title={chartTitle}
            width={width}
            height={height}
          />
        </div>
      );
    }, [renderedChart, state]);

    return <>{rendered}</>;
  };

  return { StateRenderer, setRange };
};

type ForPreviewSetter =
  | {
      tag: 'forInPreview';
      setChartDataURL: (chartDataURL: string | null) => void;
    }
  | {
      tag: 'forGetRange';
      setRange: SetRange;
    };

export const deconstructForPreviewSetter = (
  forPreviewSetter?: ForPreviewSetter
): {
  setRange: SetRange | null;
  setChartDataURL: ((chartDataURL: string | null) => void) | null;
} =>
  forPreviewSetter
    ? forPreviewSetter.tag === 'forGetRange'
      ? { setRange: forPreviewSetter.setRange, setChartDataURL: null }
      : { setChartDataURL: forPreviewSetter.setChartDataURL, setRange: null }
    : { setChartDataURL: null, setRange: null };

export type ChartForPreviewInternalProps = {
  options?: Highcharts.Options;
  forPreviewSetter: ForPreviewSetter;
};

export type ChartForPreviewProps<Data> = ComponentProps<
  ChartForPreviewType<Data>
>;
export type ChartForNormalProps = {
  setRange: SetRange;
};
